import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc, getDocs, addDoc } from 'firebase/firestore';
import { ref, get } from 'firebase/database';
import { db, auth, database } from '../firebase';
import Avatar from './Avatar';
import '../styles/ChatList.css';

const ChatList = () => {
    const [chats, setChats] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showTab, setShowTab] = useState('chats'); // 'chats' or 'contacts'
    const navigate = useNavigate();

    useEffect(() => {
        if (!auth.currentUser) {
            setLoading(false);
            return;
        }

        const currentUserId = auth.currentUser.uid;

        // Cargar chats
        const chatsQuery = query(
            collection(db, 'chats'),
            where('participants', 'array-contains', currentUserId),
            orderBy('lastMessageTimestamp', 'desc')
        );

        const unsubChats = onSnapshot(chatsQuery, async (snapshot) => {
            const chatsData = await Promise.all(snapshot.docs.map(async (chatDoc) => {
                const data = chatDoc.data();
                const otherId = data.participants.find(id => id !== currentUserId);
                const userDoc = await getDoc(doc(db, 'users', otherId));
                return { id: chatDoc.id, ...data, otherParticipant: userDoc.exists() ? userDoc.data() : { nick: 'Usuario' } };
            }));
            setChats(chatsData);
            setLoading(false);
        });

        // Cargar contactos (usuarios premium)
        const loadUsers = async () => {
            try {
                // 1. Obtener licencias premium desde RTDB
                let licensesData = {};
                try {
                    const licensesRef = ref(database, 'licencesPremium');
                    const licensesSnap = await get(licensesRef);
                    licensesData = licensesSnap.val() || {};
                } catch (rtdbErr) {
                    console.warn("[ChatList] ⚠️ No se pudieron cargar todas las licencias (requiere admin). Continuando con filtro limitado.");
                }

                // 2. Crear un Set de emails y UIDs que son premium y activos
                const premiumEmails = new Set();
                const premiumUids = new Set();
                const now = Date.now();

                Object.keys(licensesData).forEach(key => {
                    const license = licensesData[key];
                    const isActive = license.isActive !== false;
                    const isPremium = license.isPremium !== false;
                    const isNotExpired = license.premiumEndDate === 0 || license.premiumEndDate > now;

                    if (isActive && isPremium && isNotExpired) {
                        premiumEmails.add(key);
                        // Intentar capturar el UID si existe en la licencia
                        if (license.uid) premiumUids.add(license.uid);
                        if (license.userId) premiumUids.add(license.userId);
                    }
                });

                // Helper para codificar email igual que en useAuth/RTDB
                const encodeEmail = (email) => {
                    return email ? email.replace(/\./g, '_dot_').replace(/@/g, '_at_') : '';
                };

                // 3. Obtener usuarios de Firestore
                const usersSnap = await getDocs(collection(db, 'users'));

                // 4. Filtrar usuarios que tengan una licencia premium activa (por Email o por UID)
                const usersData = usersSnap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(u => {
                        if (u.id === currentUserId) return false;

                        // Estrategia 1: Match por UID (Más robusto si Firestore no tiene emails)
                        if (premiumUids.has(u.id)) {
                            return true;
                        }

                        // Estrategia 2: Match por Email (Fallback)
                        if (u.email) {
                            const encodedEmail = encodeEmail(u.email);
                            if (premiumEmails.has(encodedEmail)) return true;
                        }

                        return false;
                    });

                setUsers(usersData);
            } catch (error) {
                console.error("Error loading users:", error);
            }
        };
        loadUsers();

        return () => unsubChats();
    }, []);

    const filteredItems = (showTab === 'chats' ? chats : users).filter(item => {
        const name = showTab === 'chats' ? (item.otherParticipant?.nick || item.otherParticipant?.displayName || '') : (item.nick || item.displayName || '');
        return name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const handleChatClick = (id) => navigate(`/dashboard/chat/${id}`);

    const startPrivateChat = async (otherUser) => {
        // Encontrar chat existente o crear uno
        const existingChat = chats.find(c => c.participants.includes(otherUser.uid));
        if (existingChat) {
            navigate(`/dashboard/chat/${existingChat.id}`);
        } else {
            // Lógica para crear nuevo chat en Firestore
            const newChatRef = await addDoc(collection(db, 'chats'), {
                participants: [auth.currentUser.uid, otherUser.uid],
                lastMessage: '',
                lastMessageTimestamp: new Date(),
                unreadCount: 0
            });
            navigate(`/dashboard/chat/${newChatRef.id}`);
        }
    };

    if (loading) {
        return (
            <div className="chat-list-container">
                <div className="loading-spinner">Cargando chats...</div>
            </div>
        );
    }

    return (
        <div className="chat-list-container">
            <div className="chat-header">
                <h1>Mensajería</h1>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => setShowTab('chats')}
                        style={{ padding: '8px 16px', background: showTab === 'chats' ? 'var(--accent)' : '#333', border: 'none', borderRadius: '20px', color: 'white', cursor: 'pointer' }}
                    >
                        Conversaciones
                    </button>
                    <button
                        onClick={() => setShowTab('contacts')}
                        style={{ padding: '8px 16px', background: showTab === 'contacts' ? 'var(--accent)' : '#333', border: 'none', borderRadius: '20px', color: 'white', cursor: 'pointer' }}
                    >
                        Directorio
                    </button>
                </div>
            </div>

            <div className="search-container">
                <input
                    type="text"
                    placeholder={`Buscar en ${showTab === 'chats' ? 'conversaciones' : 'directorio'}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div>

            <div className="chats-container">
                {filteredItems.length === 0 ? (
                    <div className="no-chats">
                        <p>{showTab === 'chats' ? 'No tienes chats aún.' : 'No se encontraron usuarios.'}</p>
                    </div>
                ) : (
                    filteredItems.map(item => (
  <div
  key={item.id}
  className={`chat-item ${showTab === 'contacts' ? 'contact-item' : ''}`}
  role="button"
  tabIndex={0}
  onClick={() => showTab === 'chats' ? handleChatClick(item.id) : startPrivateChat(item)}
  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (showTab === 'chats') handleChatClick(item.id); else startPrivateChat(item); } }}
  >
                            <div className="chat-avatar">
                                <Avatar src={item.otherParticipant?.photoURL || item.avatar} size={50} />
                            </div>
                            <div className="chat-info">
                                <div className="chat-header-info">
                                    <span className="chat-name">{item.otherParticipant?.nick || item.nick || 'Usuario'}</span>
                                    {showTab === 'chats' && item.lastMessageTimestamp && (
                                        <span className="chat-time">
                                            {new Date(item.lastMessageTimestamp.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    )}
                                </div>
                                <div className="chat-preview">
                                    {showTab === 'chats' ? (
                                        item.lastMessage || <span className="no-messages">Sin mensajes</span>
                                    ) : (
                                        item.bio || 'Sin biografía'
                                    )}
                                </div>
                            </div>
                            {showTab === 'chats' && item.unreadCount > 0 && (
                                <div className="unread-badge">
                                    {item.unreadCount}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ChatList;